import React, { useState } from 'react';
import { useFunnelStore } from '../../store/funnelStore';
import ViewportSwitcher from '../Canvas/ViewportSwitcher';
import CanvasSection from '../Canvas/CanvasSection';
import {  X } from 'lucide-react';
import { PreviewIcon } from '../../assets/preview';
import RightPanel from './RightPanel';
import LeftPanel from './LeftPanel';
import { DownloadIcon } from '../../assets/download';

/**
 * Canvas Component
 * Main canvas area for previewing and editing the funnel
 */
const Canvas: React.FC = () => {
  const { funnel, currentBreakpoint, isPreviewMode, setPreviewMode } = useFunnelStore();
  const [zoom, setZoom] = useState(1);

  // Get canvas width based on breakpoint (only used in preview mode)
  const getCanvasWidth = () => {
    switch (currentBreakpoint) {
      case 'mobile':
        return 375;
      case 'tablet':
        return 768;
      case 'desktop':
      default:
        return 1000;
    }
  };

  const canvasWidth = isPreviewMode ? getCanvasWidth() : undefined;

  // const handleZoomIn = () => {
  //   setZoom((prev) => Math.min(prev + 0.1, 2));
  // };

  // const handleZoomOut = () => {
  //   setZoom((prev) => Math.max(prev - 0.1, 0.5));
  // };

  // const handleResetZoom = () => {
  //   setZoom(1);
  // };

  return (
    <div className="h-full flex flex-col bg-gray-100">
      {/* Toolbar */}
      {!isPreviewMode && (
        <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/80 px-5 py-3.5 flex items-center justify-between shadow-sm">
          <div className="flex flex-col items-start gap-0.5">
            <h2 className="text-lg font-bold text-gray-800 tracking-tight">Funnel Builder</h2>
            <p className="text-xs text-gray-500 font-medium">Landing Page</p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => {}}
              className="group p-2.5 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-all duration-200 active:scale-95"
              title="Download"
            >
              <DownloadIcon />
            </button>
            <div className="h-6 w-px bg-gray-200" />
            <button
              onClick={() => setPreviewMode(true)}
              className="group flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 active:scale-95"
              title="Preview"
            >
              <PreviewIcon />
              <span className="text-sm font-medium">Preview</span>
            </button>
            <div className="h-6 w-px bg-gray-200" />
            <button
              onClick={() => setPreviewMode(true)}
              className="h-[38px] bg-gradient-to-r from-[#003C07] to-[#004d0a] w-[106px] text-white rounded-lg hover:from-[#003C07]/90 hover:to-[#004d0a]/90 transition-all duration-200 shadow-md shadow-[#003C07]/20 active:scale-95 font-semibold text-sm"
              title="Publish"
            >
              Publish
            </button>
          </div>
        </div>
      )}

      {/* Preview Mode Toolbar */}
      {isPreviewMode && (
        <div className="bg-white/80 backdrop-blur-xl border-b border-gray-200/80 px-5 py-3.5 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
              <h2 className="text-sm font-bold text-gray-800">Preview Mode</h2>
            </div>
            <div className="px-2.5 py-1 bg-gray-100 rounded-md text-xs font-medium text-gray-600">
              {canvasWidth}px × Auto
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Viewport Switcher */}
            <ViewportSwitcher />

            {/* Exit Preview Button */}
            <button
              onClick={() => setPreviewMode(false)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-800 transition-all duration-200 active:scale-95 font-medium shadow-md"
              title="Exit Preview"
            >
              <X size={16} />
              <span>Exit Preview</span>
            </button>
          </div>
        </div>
      )}

      {/* Canvas Area */}
      <div className="flex gap-4 flex-1 overflow-auto bg-gradient-to-br from-gray-50 to-gray-100/50">
        <div className="w-[338px] bg-white/90 backdrop-blur-sm border-r border-gray-200/80 flex-shrink-0 relative shadow-sm">
          {/* Panel Indicator */}
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-primary-500/50 via-primary-400/30 to-transparent"></div>
          <RightPanel />
        </div>
        <div className="w-64 bg-white/90 backdrop-blur-sm border-r border-gray-200/80 flex-shrink-0 shadow-sm">
          <LeftPanel />
        </div>
        <div className={isPreviewMode ? "flex justify-center" : "flex-1"}>
          <div
            className="bg-white shadow-xl transition-all duration-200 border border-gray-200/50"
            style={{
              height: '85vh',
              width: isPreviewMode ? `${canvasWidth}px` : '100%',
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
            }}
          >
            {funnel.sections.length === 0 ? (
              <div className="flex items-center justify-center h-96">
                <div className="text-center space-y-3">
                  <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                    <div className="w-8 h-8 rounded-lg bg-gray-300/50"></div>
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-700 mb-1">No sections yet</p>
                    <p className="text-sm text-gray-500">Add a section from the left panel to get started</p>
                  </div>
                </div>
              </div>
            ) : (
              funnel.sections.map((section) => (
                <CanvasSection key={section.id} section={section} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Canvas;

